const CANVAS_WIDTH = 900
const CANVAS_HEIGHT = 1125

function roundedRect(context, x, y, width, height, radius) {
  const corner = Math.min(radius, width / 2, height / 2)
  context.beginPath()
  context.moveTo(x + corner, y)
  context.arcTo(x + width, y, x + width, y + height, corner)
  context.arcTo(x + width, y + height, x, y + height, corner)
  context.arcTo(x, y + height, x, y, corner)
  context.arcTo(x, y, x + width, y, corner)
  context.closePath()
}

export function changedIndex(firstWord = '', secondWord = '') {
  return [...firstWord].findIndex(
    (letter, index) => letter !== secondWord[index],
  )
}

export function drawChainSegment(context, start, end, color) {
  const dx = end.x - start.x
  const dy = end.y - start.y
  const distance = Math.hypot(dx, dy)

  if (!distance) return

  context.save()
  context.strokeStyle = color
  context.lineCap = 'round'
  context.lineWidth = 5
  context.shadowColor = color
  context.shadowBlur = 12
  context.globalAlpha = 0.42
  context.beginPath()
  context.moveTo(start.x, start.y)
  context.lineTo(end.x, end.y)
  context.stroke()

  context.globalAlpha = 1
  context.shadowBlur = 5
  context.lineWidth = 3
  const spacing = 18
  const linkCount = Math.max(1, Math.floor(distance / spacing))

  for (let index = 0; index <= linkCount; index += 1) {
    const progress = index / linkCount
    const x = start.x + dx * progress
    const y = start.y + dy * progress
    context.beginPath()
    context.ellipse(x, y, 6, 9, Math.atan2(dy, dx), 0, Math.PI * 2)
    context.stroke()
  }
  context.restore()
}

function drawChain(context, points, color) {
  points.slice(1).forEach((point, index) => {
    drawChainSegment(context, points[index], point, color)
  })
}

function drawNode(context, point, tileSize, color) {
  context.save()
  context.strokeStyle = color
  context.lineWidth = 7
  context.shadowColor = color
  context.shadowBlur = 10
  roundedRect(
    context,
    point.x - tileSize / 2,
    point.y - tileSize / 2,
    tileSize,
    tileSize,
    20,
  )
  context.stroke()
  context.restore()
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('Could not create the win image.'))
    }, 'image/png')
  })
}

export async function generateWinImage(gameState, colors) {
  const canvas = document.createElement('canvas')
  canvas.width = CANVAS_WIDTH
  canvas.height = CANVAS_HEIGHT
  const context = canvas.getContext('2d')

  const upper = gameState.upper
  const lower = [...gameState.lower].reverse()
  const rowCount = upper.length + 1 + lower.length
  const headerHeight = 205
  const footerHeight = 70
  const availableHeight = CANVAS_HEIGHT - headerHeight - footerHeight
  const rowPitch = Math.min(126, availableHeight / rowCount)
  const columnGap = 20
  const tileSize = Math.min(
    100,
    rowPitch - 20,
    (CANVAS_WIDTH - 190 - columnGap * (gameState.wordLength - 1)) /
      gameState.wordLength,
  )
  const boardHeight = (rowCount - 1) * rowPitch + tileSize
  const boardTop = headerHeight + (availableHeight - boardHeight) / 2
  const linkRowIndex = upper.length

  const rowCenter = (rowIndex) =>
    boardTop + rowIndex * rowPitch + tileSize / 2
  const columnCenter = (columnIndex) =>
    CANVAS_WIDTH / 2 +
    (columnIndex - (gameState.wordLength - 1) / 2) *
      (tileSize + columnGap)
  const point = (rowIndex, columnIndex) => ({
    x: columnCenter(columnIndex),
    y: rowCenter(rowIndex),
  })

  const background = context.createLinearGradient(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)
  background.addColorStop(0, colors.background)
  background.addColorStop(0.5, colors.surface)
  background.addColorStop(1, colors.background)
  context.fillStyle = background
  context.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

  const glow = context.createRadialGradient(
    CANVAS_WIDTH / 2,
    rowCenter(linkRowIndex),
    0,
    CANVAS_WIDTH / 2,
    rowCenter(linkRowIndex),
    420,
  )
  glow.addColorStop(0, colors.goldGlow)
  glow.addColorStop(1, 'rgba(255, 255, 255, 0)')
  context.fillStyle = glow
  context.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

  context.fillStyle = colors.text
  context.font = '900 58px Inter, system-ui, sans-serif'
  context.textAlign = 'left'
  context.fillText(`CHAINDLE ${gameState.wordLength}`, 82, 84)
  context.fillStyle = colors.muted
  context.font = '700 27px Inter, system-ui, sans-serif'
  context.fillText(`Daily puzzle #${gameState.puzzle_number}`, 84, 126)
  context.textAlign = 'right'
  context.fillStyle = colors.text
  context.font = '900 36px Inter, system-ui, sans-serif'
  const score = upper.length + lower.length - 1
  context.fillText(`${score} moves`, CANVAS_WIDTH - 82, 88)
  context.fillStyle = colors.muted
  context.font = '700 24px Inter, system-ui, sans-serif'
  context.fillText('Spoiler-free solve', CANVAS_WIDTH - 82, 126)

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
    const isStart = rowIndex === 0
    const isLink = rowIndex === linkRowIndex
    const isEnd = rowIndex === rowCount - 1
    const fill = isStart
      ? colors.primary
      : isLink
      ? colors.link
      : isEnd
      ? colors.secondary
      : colors.tile

    for (let columnIndex = 0; columnIndex < gameState.wordLength; columnIndex += 1) {
      const center = point(rowIndex, columnIndex)
      roundedRect(
        context,
        center.x - tileSize / 2,
        center.y - tileSize / 2,
        tileSize,
        tileSize,
        20,
      )
      context.fillStyle = fill
      context.fill()
      context.strokeStyle = colors.border
      context.lineWidth = 2
      context.stroke()
    }
  }

  const finalUpperIndex = changedIndex(upper[upper.length - 1][0], gameState.link)
  const finalLowerIndex = changedIndex(gameState.link, lower[0][0])

  if (upper.length > 1) {
    const upperPoints = [point(0, upper[1][1][0])]
    for (let rowIndex = 1; rowIndex < upper.length; rowIndex += 1) {
      const incomingIndex = upper[rowIndex][1][0]
      const outgoingIndex =
        rowIndex < upper.length - 1
          ? upper[rowIndex + 1][1][0]
          : finalUpperIndex
      upperPoints.push(point(rowIndex, incomingIndex))
      if (incomingIndex !== outgoingIndex) {
        upperPoints.push(point(rowIndex, outgoingIndex))
      }
    }
    drawChain(context, upperPoints, colors.primary)
    const upperNodePoints = [point(0, upper[1][1][0])]
    for (let rowIndex = 1; rowIndex < upper.length; rowIndex += 1) {
      upperNodePoints.push(point(rowIndex, upper[rowIndex][1][0]))
    }
    upperNodePoints.forEach((chainPoint) =>
      drawNode(context, chainPoint, tileSize, colors.primary),
    )
  }

  const goldPoints = [
    point(upper.length - 1, finalUpperIndex),
    point(linkRowIndex, finalUpperIndex),
  ]
  if (finalUpperIndex !== finalLowerIndex) {
    goldPoints.push(point(linkRowIndex, finalLowerIndex))
  }
  goldPoints.push(point(linkRowIndex + 1, finalLowerIndex))
  drawChain(context, goldPoints, colors.gold)
  goldPoints.forEach((chainPoint) =>
    drawNode(context, chainPoint, tileSize, colors.gold),
  )

  if (lower.length > 1) {
    const firstOutgoingIndex = lower[0][1][0]
    const lowerPoints = [point(linkRowIndex + 1, finalLowerIndex)]
    if (finalLowerIndex !== firstOutgoingIndex) {
      lowerPoints.push(point(linkRowIndex + 1, firstOutgoingIndex))
    }
    for (let rowIndex = 1; rowIndex < lower.length; rowIndex += 1) {
      const incomingIndex = lower[rowIndex - 1][1][0]
      lowerPoints.push(point(linkRowIndex + 1 + rowIndex, incomingIndex))
      if (rowIndex < lower.length - 1) {
        const outgoingIndex = lower[rowIndex][1][0]
        if (incomingIndex !== outgoingIndex) {
          lowerPoints.push(point(linkRowIndex + 1 + rowIndex, outgoingIndex))
        }
      }
    }
    drawChain(context, lowerPoints, colors.secondary)
    const lowerNodePoints = []
    for (let rowIndex = 0; rowIndex < lower.length - 1; rowIndex += 1) {
      lowerNodePoints.push(
        point(linkRowIndex + 1 + rowIndex, lower[rowIndex][1][0]),
      )
    }
    lowerNodePoints.forEach((chainPoint) =>
      drawNode(context, chainPoint, tileSize, colors.secondary),
    )
  }

  return canvasToBlob(canvas)
}

export async function shareWinImage(gameState, colors, preparedBlob = null) {
  const blob = preparedBlob || (await generateWinImage(gameState, colors))
  const filename = `chaindle-${gameState.wordLength}-${gameState.puzzle_number}.png`
  const file = new File([blob], filename, { type: 'image/png' })
  const supportsReliableMobileShare =
    navigator.maxTouchPoints > 0 &&
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)

  if (
    supportsReliableMobileShare &&
    navigator.share &&
    navigator.canShare?.({ files: [file] })
  ) {
    try {
      await navigator.share({
        files: [file],
        title: `Chaindle ${gameState.wordLength} #${gameState.puzzle_number}`,
        text: 'My spoiler-free Chaindle path',
      })
      return 'shared'
    } catch (error) {
      if (error?.name === 'AbortError') {
        return 'cancelled'
      }
      // Chromium can report file-share support and still reject the share
      // after canvas encoding. Continue to the download fallback in that case.
    }
  }

  const imageUrl = URL.createObjectURL(blob)
  const downloadLink = document.createElement('a')
  downloadLink.href = imageUrl
  downloadLink.download = filename
  document.body.appendChild(downloadLink)
  downloadLink.click()
  downloadLink.remove()
  setTimeout(() => URL.revokeObjectURL(imageUrl), 1000)
  return 'downloaded'
}
